import React, { useEffect, useState } from 'react';
import { getReportesDashboard, getIngresosMensuales, getTopClientes } from '../services/api';

const fmt = (n) => '$' + Number(n || 0).toLocaleString('es-CO');

function KPI({ label, value, sub, color }) {
  return (
    <div style={{ background:'#1e293b', borderRadius:12, padding:'20px 24px', border:'1px solid #334155', borderLeft:`4px solid ${color}` }}>
      <p style={{ color:'#64748b', fontSize:12, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:1 }}>{label}</p>
      <p style={{ color:'#f1f5f9', fontSize:28, fontWeight:700, margin:'0 0 4px' }}>{value}</p>
      {sub && <p style={{ color:'#475569', fontSize:12, margin:0 }}>{sub}</p>}
    </div>
  );
}

function BarChart({ data, labelKey, valueKey, color = '#3b82f6' }) {
  if (!data.length) return <p style={{ color:'#475569', textAlign:'center', padding:24 }}>Sin datos</p>;
  const max = Math.max(...data.map(d => Number(d[valueKey])));
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {data.map((d, i) => {
        const pct = max ? (Number(d[valueKey]) / max) * 100 : 0;
        return (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ color:'#94a3b8', fontSize:12, minWidth:120, textAlign:'right', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d[labelKey]}</span>
            <div style={{ flex:1, background:'#0f172a', borderRadius:4, height:20, overflow:'hidden' }}>
              <div style={{ width:`${pct}%`, background:color, height:'100%', borderRadius:4, transition:'width .3s', minWidth:4 }} />
            </div>
            <span style={{ color:'#f1f5f9', fontSize:12, minWidth:90, textAlign:'right' }}>{fmt(d[valueKey])}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function Reportes() {
  const [data,     setData]     = useState(null);
  const [ingresos, setIngresos] = useState([]);
  const [topCli,   setTopCli]   = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      getReportesDashboard(),
      getIngresosMensuales(),
      getTopClientes(),
    ]).then(([d, i, t]) => {
      setData(d.data);
      setIngresos(i.data);
      setTopCli(t.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color:'#64748b', textAlign:'center', paddingTop:80, fontSize:14 }}>Cargando reportes...</div>;
  if (!data)   return <div style={{ color:'#ef4444', textAlign:'center', paddingTop:80 }}>Error al cargar datos</div>;

  const totalFacturado = Number(data.facturacion?.total_facturado || 0);
  const saldoPendiente = Number(data.facturacion?.saldo_pendiente || 0);
  const totalCobrado   = totalFacturado - saldoPendiente;
  const totalCompras   = Number(data.compras?.total_compras || 0);

  const proyectosActivos = data.proyectos?.find(p => p.estado === 'activo')?.total || 0;
  const proyectos        = data.proyectos?.reduce((s, p) => s + Number(p.total), 0) || 0;
  const cotAprobadas     = data.cotizaciones?.find(c => c.estado === 'aprobada')?.total || 0;

  const pendientesOT = data.ordenesTrabajo?.filter(o => o.estado !== 'cerrada').reduce((s, o) => s + Number(o.total), 0) || 0;
  const pendientesMnt = data.mantenimientos?.filter(o => o.estado !== 'completada').reduce((s, o) => s + Number(o.total), 0) || 0;

  const mesesLabel = ingresos.map(r => {
    const d = new Date(r.mes);
    return d.toLocaleDateString('es-CO', { month:'short', year:'2-digit' });
  });

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ color:'#f1f5f9', fontSize:24, fontWeight:700, margin:'0 0 4px' }}>Reportes</h1>
        <p style={{ color:'#64748b', fontSize:14, margin:0 }}>Histórico de la empresa (todos los años)</p>
      </div>

      {/* KPIs financieros */}
      <div style={s.kpiGrid}>
        <KPI label="Total facturado" value={fmt(totalFacturado)} sub="Todas las facturas activas" color="#3b82f6" />
        <KPI label="Total cobrado"   value={fmt(totalCobrado)}  sub="Pagos recibidos"             color="#10b981" />
        <KPI label="Saldo pendiente" value={fmt(saldoPendiente)} sub="Por cobrar"                 color="#f59e0b" />
        <KPI label="Total compras"   value={fmt(totalCompras)}  sub="OC no canceladas"            color="#8b5cf6" />
      </div>

      {/* KPIs operativos */}
      <div style={{ ...s.kpiGrid, marginTop:16 }}>
        <KPI label="Proyectos activos"    value={proyectosActivos} sub={`De ${proyectos} en total`} color="#06b6d4" />
        <KPI label="Cotiz. aprobadas"     value={cotAprobadas}     sub="Listas para proyectar"      color="#10b981" />
        <KPI label="OT pendientes"        value={pendientesOT}     sub="Abiertas + en progreso"     color="#f59e0b" />
        <KPI label="Mant. pendientes"     value={pendientesMnt}    sub="Pendientes + en progreso"   color="#ef4444" />
      </div>

      {/* Gráficas */}
      <div style={s.chartsGrid}>
        {/* Ingresos mensuales */}
        <div style={s.chartCard}>
          <h3 style={s.chartTitle}>Facturación mensual (12 meses)</h3>
          {ingresos.length === 0
            ? <p style={{ color:'#475569', textAlign:'center', padding:24, fontSize:13 }}>Sin datos de facturación</p>
            : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {ingresos.map((r, i) => {
                  const max = Math.max(...ingresos.map(x => Number(x.total_facturado)));
                  const pct = max ? (Number(r.total_facturado) / max) * 100 : 0;
                  const cobPct = Number(r.total_facturado) ? (Number(r.total_cobrado) / Number(r.total_facturado)) * 100 : 0;
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ color:'#94a3b8', fontSize:11, minWidth:50 }}>{mesesLabel[i]}</span>
                      <div style={{ flex:1, background:'#0f172a', borderRadius:4, height:18, overflow:'hidden', position:'relative' }}>
                        <div style={{ width:`${pct}%`, background:'#1e40af', height:'100%', position:'absolute' }} />
                        <div style={{ width:`${(pct*cobPct/100)}%`, background:'#3b82f6', height:'100%', position:'absolute' }} />
                      </div>
                      <span style={{ color:'#f1f5f9', fontSize:11, minWidth:80, textAlign:'right' }}>{fmt(r.total_facturado)}</span>
                    </div>
                  );
                })}
                <div style={{ display:'flex', gap:16, marginTop:8 }}>
                  <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#94a3b8' }}><span style={{ width:10, height:10, borderRadius:2, background:'#1e40af', display:'inline-block' }} />Facturado</span>
                  <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#94a3b8' }}><span style={{ width:10, height:10, borderRadius:2, background:'#3b82f6', display:'inline-block' }} />Cobrado</span>
                </div>
              </div>
            )
          }
        </div>

        {/* Top clientes */}
        <div style={s.chartCard}>
          <h3 style={s.chartTitle}>Top clientes por facturación</h3>
          <BarChart data={topCli} labelKey="razon_social" valueKey="total_facturado" color="#10b981" />
        </div>
      </div>

      {/* Facturas próximas a vencer */}
      {data.facturasPendientes?.length > 0 && (
        <div style={s.alertCard}>
          <h3 style={{ ...s.chartTitle, color:'#fbbf24' }}>Facturas pendientes próximas a vencer</h3>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{['Número','Cliente','Total','Saldo','Vencimiento'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.facturasPendientes.map((f, i) => (
                <tr key={i}>
                  <td style={{ ...s.td, color:'#93c5fd' }}>{f.numero}</td>
                  <td style={s.td}>{f.cliente}</td>
                  <td style={s.td}>{fmt(f.total)}</td>
                  <td style={{ ...s.td, color:'#f59e0b', fontWeight:600 }}>{fmt(f.saldo)}</td>
                  <td style={s.td}>{f.fecha_vencimiento ? new Date(f.fecha_vencimiento).toLocaleDateString('es-CO') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const s = {
  kpiGrid:    { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 },
  chartsGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginTop:24 },
  chartCard:  { background:'#1e293b', borderRadius:12, padding:'20px 24px', border:'1px solid #334155' },
  alertCard:  { background:'#1e293b', borderRadius:12, padding:'20px 24px', border:'1px solid #92400e', marginTop:16 },
  chartTitle: { color:'#f1f5f9', fontSize:14, fontWeight:600, margin:'0 0 16px' },
  th:         { padding:'10px 12px', color:'#64748b', fontSize:12, textAlign:'left', borderBottom:'1px solid #334155' },
  td:         { padding:'4px 12px', color:'#cbd5e1', fontSize:13, borderBottom:'1px solid #1a2332' },
};
